const stripHtml = (html) => {
  if (!html) return "";
  return String(html).replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
};

export const buildCandidateData = (resumeData) => {
  const {
    resumedetails = {},
    skillsdetails = [],
    projectdetails = [],
    educationdetails = [],
    certificatedetails = [],
  } = resumeData || {};

  const skillsFromDetails = (skillsdetails || []).map((item) => ({
    category: item.category_name || item.category || "Skills",
    skills: Array.isArray(item.skills)
      ? item.skills
      : String(item.skills || item.skill_names || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
  }));

  const aiSkills = resumedetails?.ai_skills
    ? String(resumedetails.ai_skills)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];

  const skills =
    skillsFromDetails.length > 0
      ? skillsFromDetails
      : aiSkills.length > 0
        ? [{ category: "Technical Skills", skills: aiSkills }]
        : [];

  return {
    summary: stripHtml(resumedetails?.summary),
    skills,
    project: (projectdetails || []).map((p) => ({
      projectTitle: p.pro_title || "",
      company: p.pro_client || "",
      description: stripHtml(p.pro_description),
      responsibilities: stripHtml(p.pro_responsibilities),
    })),
    education: (educationdetails || []).map((e) => ({
      university: e.edu_university || "",
      yearofpassing: e.edu_passing_year || "",
      degree: e.edu_course || "",
    })),
    certificate: (certificatedetails || []).map((c) => ({
      certName: c.cer_name || "",
      certYear: c.cer_year || "",
    })),
  };
};

export const flattenOptimizedSkills = (optimizedData) => {
  const groups = optimizedData?.skills;
  if (!Array.isArray(groups)) return [];

  return groups.flatMap((group) => {
    if (Array.isArray(group?.skills)) {
      return group.skills.map((skill) => String(skill).trim()).filter(Boolean);
    }
    if (typeof group?.skills === "string") {
      return group.skills
        .split(",")
        .map((skill) => skill.trim())
        .filter(Boolean);
    }
    return [];
  });
};

export const normalizeProjectContent = (project = {}) => ({
  projectTitle: project.projectTitle || project.pro_title || "",
  company: project.company || project.pro_client || "",
  description: project.description || project.pro_description || "",
  responsibilities: project.responsibilities || project.pro_responsibilities || "",
});

export const mapOptimizedProjectToDetail = (project, original = {}) => {
  const normalized = normalizeProjectContent(project);
  return {
    ...original,
    pro_title: normalized.projectTitle,
    pro_client: normalized.company,
    pro_description: normalized.description,
    pro_responsibilities: normalized.responsibilities,
  };
};

export const mergeProjectDetails = (existingProjects = [], optimizedProjects = []) => {
  if (!Array.isArray(optimizedProjects) || optimizedProjects.length === 0) {
    return existingProjects;
  }

  return optimizedProjects.map((project, index) =>
    mapOptimizedProjectToDetail(project, existingProjects[index] || {})
  );
};

export const mapOptimizedToResumeFullDetails = (originalResumeData, optimizedData) => {
  const { resumedetails = {}, projectdetails = [] } = originalResumeData || {};
  const allSkills = flattenOptimizedSkills(optimizedData);

  const mappedProjects = mergeProjectDetails(projectdetails, optimizedData?.project || []);

  return {
    resumedetails: {
      ...resumedetails,
      summary: optimizedData?.summary || "",
      ai_skills: allSkills.join(", "),
    },
    projectdetails: mappedProjects,
    educationdetails: (optimizedData?.education || []).map((item) => ({
      edu_university: item.university || "",
      edu_passing_year: item.yearofpassing || "",
      edu_course: item.degree || "",
    })),
    certificatedetails: (optimizedData?.certificate || []).map((item) => ({
      cer_name: item.certName || "",
      cer_year: item.certYear || "",
    })),
    skillsdetails: (optimizedData?.skills || []).map((item) => ({
      category_name: item.category || "Skills",
      skills: Array.isArray(item.skills) ? item.skills.join(", ") : item.skills || "",
    })),
  };
};

export const getJobDescriptionText = (job) => {
  if (!job) return "";
  const raw = job.job_description || job.description || "";
  return stripHtml(raw);
};

export const mergeOptimizedDataIntoResumeFullDetails = (resumeFullDetails, optimizedData) => {
  const base = resumeFullDetails || {};
  const allSkills = flattenOptimizedSkills(optimizedData);
  const existingProjects = base.projectdetails || [];

  return {
    ...base,
    resumedetails: {
      ...(base.resumedetails || {}),
      summary: optimizedData?.summary || "",
      ai_skills: allSkills.join(", "),
    },
    projectdetails: mergeProjectDetails(existingProjects, optimizedData?.project || []),
    educationdetails: (optimizedData?.education || []).map((item) => ({
      edu_university: item.university || "",
      edu_passing_year: item.yearofpassing || "",
      edu_course: item.degree || "",
    })),
    certificatedetails: (optimizedData?.certificate || []).map((item) => ({
      cer_name: item.certName || "",
      cer_year: item.certYear || "",
    })),
    skillsdetails: (optimizedData?.skills || []).map((item) => ({
      category_name: item.category || "Skills",
      skills: Array.isArray(item.skills) ? item.skills.join(", ") : item.skills || "",
    })),
  };
};

export const normalizeSkillsContent = (updatedContent) => {
  if (Array.isArray(updatedContent)) return updatedContent;
  if (updatedContent?.skills) return [updatedContent];
  return [{ category: "Technical Skills", skills: [] }];
};

export const normalizeEducationItem = (item) => {
  if (typeof item === "string") {
    return { university: "", yearofpassing: "", degree: item };
  }
  return {
    university: item.university || item.edu_university || item.school || item.college || item.institution || "",
    yearofpassing: item.yearofpassing || item.edu_passing_year || item.year || item.graduationYear || "",
    degree: item.degree || item.edu_course || item.course || item.qualification || "",
  };
};

export const normalizeEducationContent = (updatedContent) => {
  if (!Array.isArray(updatedContent)) return [];
  return updatedContent.map(normalizeEducationItem);
};

export const normalizeCertificateItem = (item) => {
  if (typeof item === "string") {
    return { certName: item, certYear: "" };
  }
  return {
    certName: item.certName || item.cer_name || item.name || item.certificateName || item.certificate || "",
    certYear: item.certYear || item.cer_year || item.year || "",
  };
};

export const normalizeCertificateContent = (updatedContent) => {
  if (!Array.isArray(updatedContent)) return [];
  return updatedContent.map(normalizeCertificateItem);
};

export const getSectionOptions = (optimizedData) => {
  if (!optimizedData) return [];

  const options = [
    { key: "summary", label: "Professional Summary", sectionName: "Summary" },
    { key: "skills", label: "Technical Skills", sectionName: "Skills" },
  ];

  (optimizedData.project || []).forEach((project, index) => {
    options.push({
      key: `project-${index}`,
      label: `Project: ${project.projectTitle || `Project ${index + 1}`}`,
      sectionName: `Project - ${project.projectTitle || `Project ${index + 1}`}`,
    });
  });

  options.push(
    { key: "education", label: "Education", sectionName: "Education" },
    { key: "certificate", label: "Certifications", sectionName: "Certifications" }
  );

  return options;
};

export const getSectionContent = (optimizedData, sectionKey) => {
  if (!optimizedData || !sectionKey) return null;

  if (sectionKey === "summary") return optimizedData.summary || "";
  if (sectionKey === "skills") return optimizedData.skills || [];
  if (sectionKey === "education") return optimizedData.education || [];
  if (sectionKey === "certificate") return optimizedData.certificate || [];

  if (sectionKey.startsWith("project-")) {
    const index = Number(sectionKey.split("-")[1]);
    return optimizedData.project?.[index] || null;
  }

  return null;
};

export const applyInlineEditResult = (optimizedData, sectionKey, updatedContent) => {
  const next = { ...optimizedData };

  if (sectionKey === "summary") {
    next.summary =
      typeof updatedContent === "string"
        ? updatedContent
        : updatedContent?.summary || String(updatedContent || "");
  } else if (sectionKey === "skills") {
    next.skills = normalizeSkillsContent(updatedContent);
  } else if (sectionKey === "education") {
    next.education = normalizeEducationContent(updatedContent);
  } else if (sectionKey === "certificate") {
    next.certificate = normalizeCertificateContent(updatedContent);
  } else if (sectionKey.startsWith("project-")) {
    const index = Number(sectionKey.split("-")[1]);
    const projects = [...(next.project || [])];
    projects[index] = normalizeProjectContent(updatedContent);
    next.project = projects;
  }

  return next;
};

export { stripHtml };
